import { ReactNode } from "react"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import { useTranslation } from "react-i18next"
import PageTitle from "features/navigation/pageTitle"
import RightSidebar from "@/components/sidebar/right/RightSidebar"
import Hint from "@/components/text/Hint"

const QueryExample = ({ query, children }: { query: string; children: ReactNode }) => (
    <div className="flex flex-col gap-y-1">
        <code className="w-fit max-w-full overflow-x-auto rounded border border-gray-300 bg-gray-100 px-2 py-1 font-mono text-sm">
            {query}
        </code>
        <Hint>{children}</Hint>
    </div>
)

const SearchTutorialPage = () => {
    const { t } = useTranslation()

    return (
        <>
            <PageTitle title={t("searchTutorialHeading")} />
            <Section>
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h1 className="whitespace-normal">{t("searchTutorialHeading")}</h1>
                    </SectionHeaderTitle>
                </SectionHeader>
                <p>{t("searchTutorialIntro")}</p>
                <p>{t("searchTutorialFocus")}</p>
            </Section>

            <Section>
                <h2 className="whitespace-normal">{t("searchTutorialTypesTitle")}</h2>
                <p>{t("searchTutorialTypesIntro")}</p>
                <ul className="flex max-w-xl list-disc flex-col gap-y-2 pl-6">
                    <li>
                        <span className="font-semibold">{t("labelProducts")}. </span>
                        {t("searchTutorialTypeProducts")}
                    </li>
                    <li>
                        <span className="font-semibold">{t("labelCategories")}. </span>
                        {t("searchTutorialTypeCategories")}
                    </li>
                    <li>
                        <span className="font-semibold">{t("labelLocations")}. </span>
                        {t("searchTutorialTypeLocations")}
                    </li>
                    <li>
                        <span className="font-semibold">{t("labelCustomers")}. </span>
                        {t("searchTutorialTypeCustomers")}
                    </li>
                    <li>
                        <span className="font-semibold">{t("labelSuppliers")}. </span>
                        {t("searchTutorialTypeSuppliers")}
                    </li>
                    <li>
                        <span className="font-semibold">{t("labelTransactions")}. </span>
                        {t("searchTutorialTypeTransactions")}
                    </li>
                </ul>
                <Hint>{t("searchTutorialCreateFromSearch")}</Hint>
            </Section>

            <Section>
                <h2 className="whitespace-normal">{t("searchTutorialSimpleTitle")}</h2>
                <p>{t("searchTutorialSimpleIntro")}</p>
                <p>{t("searchTutorialSimpleProducts")}</p>
                <p>{t("searchTutorialSimpleFieldsLead")}</p>
                <ul className="flex max-w-xl list-disc flex-col gap-y-1 pl-6">
                    <li>{t("labelSKU")}</li>
                    <li>{t("name")}</li>
                    <li>{t("description")}</li>
                    <li>{t("searchTutorialSimpleLocation")}</li>
                    <li>{t("unit")}</li>
                    <li>{t("labelBarcode")}</li>
                    <li>{t("searchTutorialSimpleCustomFields")}</li>
                </ul>
                <p>{t("searchTutorialSimpleCategory")}</p>
                <p>{t("searchTutorialSimpleOther")}</p>
                <QueryExample query="bolt">{t("searchExampleSimple")}</QueryExample>
                <QueryExample query="bolt, M8">{t("searchExampleAnd")}</QueryExample>
            </Section>

            <Section>
                <h2 className="whitespace-normal">{t("searchTutorialTargetedTitle")}</h2>
                <p>{t("searchTutorialTargetedIntro")}</p>
                <p>{t("searchTutorialTargetedAnd")}</p>
                <p>{t("searchTutorialTargetedIgnore")}</p>
                <p>{t("searchTutorialAttributesLead")}</p>
                <div className="flex max-w-xl flex-col gap-y-3">
                    <QueryExample query="sku:AB-12">{t("searchTutorialAttrSku")}</QueryExample>
                    <QueryExample query="name:bolt">{t("searchTutorialAttrName")}</QueryExample>
                    <QueryExample query="description:steel">{t("searchTutorialAttrDescription")}</QueryExample>
                    <QueryExample query="location:shelf">{t("searchTutorialAttrLocation")}</QueryExample>
                    <QueryExample query="unit:pcs">{t("searchTutorialAttrUnit")}</QueryExample>
                    <QueryExample query="category:fasteners">{t("searchTutorialAttrCategory")}</QueryExample>
                </div>
                <h3 className="whitespace-normal">{t("searchTutorialCustomTitle")}</h3>
                <p>{t("searchTutorialCustomBody")}</p>
                <p>{t("searchTutorialCustomTypes")}</p>
                <p>{t("searchTutorialCustomCheckbox")}</p>
                <QueryExample query="color:red">{t("searchExampleCustom")}</QueryExample>
                <QueryExample query="weight:2.5">{t("searchExampleNumber")}</QueryExample>
                <QueryExample query="fragile:true">{t("searchExampleCheckbox")}</QueryExample>
                <QueryExample query="name:bolt, category:fasteners">{t("searchExampleCombined")}</QueryExample>
                <Hint>{t("searchTutorialCustomColumns")}</Hint>
            </Section>

            <Section>
                <h2 className="whitespace-normal">{t("searchTutorialDeletedTitle")}</h2>
                <p>
                    {t("searchTutorialDeletedBody", {
                        help: t("titleHelpAndSettings"),
                        options: t("titleSearchOptions"),
                        deleted: t("labelDisplayDeleted"),
                    })}
                </p>
                <h2 className="whitespace-normal">{t("searchTutorialLimitTitle")}</h2>
                <p>{t("searchTutorialLimitBody")}</p>
            </Section>
            <RightSidebar />
        </>
    )
}

export default SearchTutorialPage
